import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, PaperPlaneRight } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { isEmailNotVerifiedError, showEmailVerificationRequiredToast } from '../utils/emailVerification';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MessageThreadPage = () => {
  const { listingId, userId } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [listing, setListing] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const messagesEndRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/messages/thread/${listingId}/${userId}`, { withCredentials: true });
      setMessages(data);
      if (data.length > 0) {
        const otherUserId = data[0].sender_id === user.user_id ? data[0].receiver_id : data[0].sender_id;
        const userRes = await axios.get(`${API}/listings/${listingId}`);
        if (userRes.data.user_id === otherUserId) {
          setOtherUser({ name: userRes.data.user_name, email: userRes.data.user_email });
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  }, [listingId, userId, user?.user_id]);

  const fetchListing = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/listings/${listingId}`);
      setListing(data);
    } catch (error) {
      console.error('Error fetching listing:', error);
    }
  }, [listingId]);

  useEffect(() => {
    fetchMessages();
    fetchListing();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [fetchMessages, fetchListing]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e, overrideContent) => {
    if (e?.preventDefault) e.preventDefault();
    const content = (overrideContent ?? newMessage).trim();
    if (!content) return;

    setSending(true);
    try {
      await axios.post(
        `${API}/messages`,
        { listing_id: listingId, receiver_id: userId, content },
        { withCredentials: true }
      );
      // Only clear the input when we actually used it (not for quick replies)
      if (overrideContent === undefined) setNewMessage('');
      fetchMessages();
    } catch (error) {
      if (isEmailNotVerifiedError(error)) {
        showEmailVerificationRequiredToast(t);
      } else {
        toast.error('Failed to send message');
      }
    } finally {
      setSending(false);
    }
  };

  const sendQuickReply = (content) => {
    if (sending) return;
    handleSend(null, content);
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col pb-20 md:pb-0">
      <Header />

      <div className="max-w-4xl mx-auto w-full flex-1 flex flex-col">
        {listing && (
          <div className="bg-white border-b border-border p-4" data-testid="thread-header">
            <Link to="/messages" className="flex items-center gap-2 text-text_muted hover:text-primary mb-3">
              <ArrowLeft size={20} />
              <span>{t('messages.backToMessages')}</span>
            </Link>
            <Link to={`/listings/${listing.listing_id}`} className="flex gap-4 items-center">
              {listing.images && listing.images.length > 0 && (
                <img
                  src={`${API}/files/${listing.images[0]}`}
                  alt={listing.title}
                  className="w-16 h-16 object-cover rounded-md"
                />
              )}
              <div>
                <h2 className="font-semibold font-heading" data-testid="thread-listing-title">{listing.title}</h2>
                {otherUser && (
                  <p className="text-sm text-text_muted" data-testid="thread-other-user">
                    Chatting with {otherUser.name}
                  </p>
                )}
              </div>
            </Link>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="messages-container">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-text_muted">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-text_muted">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isMine = msg.sender_id === user.user_id;
              return (
                <div
                  key={msg.message_id}
                  className={`flex gap-3 ${isMine ? 'flex-row-reverse' : ''}`}
                  data-testid={`message-${msg.message_id}`}
                >
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="text-xs bg-primary text-white">
                      {isMine ? user.name?.charAt(0).toUpperCase() : otherUser?.name?.charAt(0).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className={`max-w-[70%] ${isMine ? 'text-right' : ''}`}>
                    <div
                      className={`inline-block px-4 py-2 rounded-lg ${
                        isMine ? 'bg-primary text-white' : 'bg-white border border-border'
                      }`}
                    >
                      <p className="whitespace-pre-wrap" data-testid="message-content">{msg.content}</p>
                    </div>
                    <p className="text-xs text-text_muted mt-1">
                      {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="bg-white border-t border-border p-4">
          {/* Quick replies — sent instantly on click. Hidden once the user
              starts typing so they don't overlap an in-progress draft. */}
          {!newMessage.trim() && (
            <div
              className="flex gap-2 overflow-x-auto pb-2 mb-2 -mx-1 px-1 scrollbar-thin"
              data-testid="quick-replies"
            >
              {(t('messages.quickReplies', { returnObjects: true }) || []).map((reply, idx) => (
                <button
                  key={reply}
                  type="button"
                  onClick={() => sendQuickReply(reply)}
                  disabled={sending}
                  className="flex-shrink-0 px-3.5 py-1.5 text-xs rounded-full border border-border bg-white text-text_main hover:border-primary/60 hover:bg-primary/5 hover:text-primary transition-colors disabled:opacity-50 whitespace-nowrap"
                  data-testid={`quick-reply-${idx}`}
                >
                  {reply}
                </button>
              ))}
            </div>
          )}
          <form onSubmit={handleSend} className="flex gap-3" data-testid="message-form">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              rows={2}
              className="flex-1 border border-border focus:border-primary focus:ring-1 focus:ring-primary"
              data-testid="message-input"
            />
            <Button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className="self-end bg-primary hover:bg-primary-hover text-white"
              data-testid="send-button"
            >
              <PaperPlaneRight size={20} weight="bold" />
            </Button>
          </form>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};